import { IRecoveryWidgetData, IRecoveryWidgetShortData, RecoveryWidgetData, TranslateService } from "@pageBuilder/typings";
import { Base64Util } from "../../../../utils/base64-util";
import { RecoveryScriptRenderer } from "./recovery-script-renderer";
import { IRenderingCommonContext } from "@commonRendering/types";
import { FormTruCaptchaScriptRenderer } from "../../form-widget/form-trucaptcha-script.renderer";
import { FormReCaptchaScriptRenderer } from "../../form-widget/form-recaptcha-script.renderer";
import { MembersAreaWidgetRendererBase } from "../common/members-area-widget-renderer-base";
import { DomUtil } from "src/utils/dom-util";
import { FormCaptchaRenderer } from "../../form-widget/form-captcha-renderer";

export class RecoveryWidgetRenderer extends MembersAreaWidgetRendererBase<IRecoveryWidgetData> {

    protected readonly DATA_ATTRIBUTE = "data-recovery";
    private readonly scriptRenderer: RecoveryScriptRenderer;
    private readonly recaptchaScriptRenderer: FormReCaptchaScriptRenderer;
    private readonly truCaptchaScriptRenderer: FormTruCaptchaScriptRenderer;
    private readonly captchaRenderer: FormCaptchaRenderer;

    private readonly translate: any;

    constructor(renderingCommonContext: IRenderingCommonContext) {
        super(renderingCommonContext);
        this.scriptRenderer = new RecoveryScriptRenderer();
        this.recaptchaScriptRenderer = new FormReCaptchaScriptRenderer();
        this.truCaptchaScriptRenderer = new FormTruCaptchaScriptRenderer();
        this.captchaRenderer = new FormCaptchaRenderer();

        this.translate = TranslateService.translator(renderingCommonContext.data.lang);
    }

    protected createNewInternal(): HTMLElement {
        return this.createWidgetElement("recoveryWidget");
    }

    protected override renderInternal(element: HTMLElement, data: IRecoveryWidgetData): HTMLElement {
        this.renderContent(element, data);
        const existingData = this.getData(element) as IRecoveryWidgetData;
        this.setAttributes(element, data);

        this.scriptRenderer.renderCommon(this.renderedDocument);
        if (RecoveryWidgetData.localScriptRerenderNeeded(existingData, data)) {
            this.scriptRenderer.renderLocal(element);
        }

        this.renderStyles(element, data);
        this.renderCaptcha(element, data.disableReCaptcha);

        return element;
    }

    private renderContent(element: HTMLElement, data: IRecoveryWidgetData): void {
        const content = element.querySelector<HTMLElement>(".widget-content");
        if (!content) {
            return;
        }

        DomUtil.deleteAllChildElements(content);

        this.renderTitle(content);
        this.renderSubtitle(content);
        this.renderRecoveryForm(content, data);
        this.renderSignInLink(content);
    }

    protected override setAttributes(element: HTMLElement, data: IRecoveryWidgetData): void {
        const dataForSave = this.prepareDataForSave(data);
        const json = Base64Util.serialize(dataForSave) || "";

        element.setAttribute(this.DATA_ATTRIBUTE, json);
        element.toggleAttribute("data-primary-element", data.primaryElement);
        if (this.isEditorRendering) {
            element.setAttribute("data-draft", "true");
        }
    }

    protected override prepareDataForSave(data: IRecoveryWidgetData): IRecoveryWidgetShortData {
        return {
            fields: data.fields,
            submit: data.submit
        } as IRecoveryWidgetShortData;
    }

    protected override getContentSelectorsInternal(element: HTMLElement): Array<string> {
        return [`#${element.id}`];
    }

    protected renderCaptcha(element: HTMLElement, disableReCaptcha: boolean | undefined): void {
        if (this.isEditorRendering || this.isPreviewRendering || disableReCaptcha) {
            return;
        }

        this.renderCaptchaScript(element);

        this.captchaRenderer.render(element, this.renderingCommonContext.data.captchaInfo);
    }

    protected renderCaptchaScript(element: HTMLElement): HTMLElement {
        this.cleanupScripts();

        if (this.renderingCommonContext.data.captchaInfo.captchaProtectionType === "TruCaptcha") {
            this.truCaptchaScriptRenderer.renderCommon(this.renderedDocument);
        } else if (this.renderingCommonContext.data.captchaInfo.captchaProtectionType === "ReCaptcha"){
            this.recaptchaScriptRenderer.renderCommon(this.renderedDocument);
        }

        return element;
    }

    private cleanupScripts(): void {
        this.truCaptchaScriptRenderer.cleanupCommon(this.renderedDocument);
        this.recaptchaScriptRenderer.cleanupCommon(this.renderedDocument);
    }

    private renderRecoveryForm(container: HTMLElement, data: IRecoveryWidgetData): void {
        const form = this.ensureFormElement(container);
        if (!form) {
            return;
        }

        const emailFieldData = data.fields?.find(x => x.type === "Email");
        const labelsStyle = data.style.labels?.style ?? "standard";

        this.renderControl(form, emailFieldData, {
            inputName: "register-email",
            inputType: "email",
            inputMaxLength: this.EMAIL_MAX_LENGTH,
            autocomplete: this.isEditorRendering ? "off" : "email",
            labelText: emailFieldData?.caption ?? this.translate.RECOVERY.DEFAULT_EMAIL_CAPTION,
            labelStyle: labelsStyle,
            withSafariAutofill: false
        });

        this.renderSubmitButton(form, data.style.submit?.theme ?? "primary", data.submit.text);
    }

    private renderTitle(container: HTMLElement): void {
        const title = document.createElement("span");
        title.classList.add("title");
        title.textContent = this.translate.RECOVERY.DEFAULT_TITLE;
        title.title = title.textContent ?? "";

        container.appendChild(title);
    }

    private renderSubtitle(container: HTMLElement): void {
        const subtitle = document.createElement("span");
        subtitle.classList.add("subtitle");
        subtitle.textContent = this.translate.RECOVERY.DEFAULT_SUBTITLE;
        subtitle.title = subtitle.textContent ?? "";

        container.appendChild(subtitle);
    }

    private renderSignInLink(container: HTMLElement): void {
        const anchor = document.createElement("a");
        anchor.classList.add("return-to-sign-in-link");
        anchor.innerText = this.translate.RECOVERY.RETURN_TO_LOG_IN;

        container.appendChild(anchor);
    }

}
